// src/pages/finance-portal/contractor-payments/ContractorPaymentForm.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Autocomplete, CircularProgress, Box, Typography, Grid,
} from '@mui/material';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_METHODS = [
    { value: 'ACH', label: 'ACH' },
    { value: 'CHECK', label: 'Check' },
    { value: 'WIRE', label: 'Wire Transfer' },
];

export default function ContractorPaymentForm({ open, onClose, onSaved, editData }) {
    const [form, setForm] = useState({
        contractor_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        description: '',
        invoice_number: '',
        payment_method: 'ACH',
        tax_year: new Date().getFullYear(),
        gl_expense_account_id: '',
        gl_cash_account_id: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Contractor autocomplete
    const [contractors, setContractors] = useState([]);
    const [contractorSearch, setContractorSearch] = useState('');
    const [contractorLoading, setContractorLoading] = useState(false);
    const [selectedContractor, setSelectedContractor] = useState(null);
    const searchTimer = useRef(null);

    // GL accounts
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [cashAccounts, setCashAccounts] = useState([]);

    useEffect(() => {
        if (open) {
            if (editData) {
                setForm({
                    contractor_id: editData.contractor_id || '',
                    amount: editData.amount || '',
                    payment_date: editData.payment_date || new Date().toISOString().split('T')[0],
                    description: editData.description || '',
                    invoice_number: editData.invoice_number || '',
                    payment_method: editData.payment_method || 'ACH',
                    tax_year: editData.tax_year || new Date().getFullYear(),
                    gl_expense_account_id: editData.gl_expense_account_id || '',
                    gl_cash_account_id: editData.gl_cash_account_id || '',
                });
                if (editData.contractor) {
                    setSelectedContractor(editData.contractor);
                }
            } else {
                setForm({
                    contractor_id: '',
                    amount: '',
                    payment_date: new Date().toISOString().split('T')[0],
                    description: '',
                    invoice_number: '',
                    payment_method: 'ACH',
                    tax_year: new Date().getFullYear(),
                    gl_expense_account_id: '',
                    gl_cash_account_id: '',
                });
                setSelectedContractor(null);
            }
            setError(null);
            fetchGLAccounts();
        }
    }, [open, editData]);

    // Contractor search with debounce
    useEffect(() => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        setContractorLoading(true);
        searchTimer.current = setTimeout(async () => {
            try {
                const params = { per_page: 50, worker_type: 'CONTRACTOR' };
                if (contractorSearch && contractorSearch.length >= 2) params.search = contractorSearch;
                const res = await axiosInstance.get('/api/finance/payroll/employees', { params });
                if (res.data.success) {
                    setContractors(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search contractors', err);
            } finally {
                setContractorLoading(false);
            }
        }, contractorSearch ? 300 : 0);
        return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
    }, [contractorSearch]);

    const fetchGLAccounts = async () => {
        try {
            const res = await axiosInstance.get('/api/finance/chart-of-accounts', { params: { per_page: 500 } });
            if (res.data.success) {
                const accounts = res.data.data || [];
                setExpenseAccounts(accounts.filter(a => a.account_type === 'EXPENSE'));
                setCashAccounts(accounts.filter(a => a.account_type === 'ASSET' || a.account_type === 'BANK'));
            }
        } catch (err) {
            console.error('Failed to load GL accounts', err);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...form,
                amount: parseFloat(form.amount) || 0,
                tax_year: parseInt(form.tax_year, 10),
                gl_expense_account_id: form.gl_expense_account_id || null,
                gl_cash_account_id: form.gl_cash_account_id || null,
            };
            if (editData && editData.id) {
                await axiosInstance.put(`/api/finance/payroll/contractor-payments/${editData.id}`, payload);
            } else {
                await axiosInstance.post('/api/finance/payroll/contractor-payments', payload);
            }
            onSaved && onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save payment');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                {editData ? 'Edit Contractor Payment' : 'New Contractor Payment'}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef2f2', borderRadius: '8px' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <Autocomplete
                            size="small"
                            options={contractors}
                            getOptionLabel={opt => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || '' : ''}
                            value={selectedContractor}
                            onChange={(_, val) => {
                                setSelectedContractor(val);
                                handleChange('contractor_id', val ? val.id : '');
                            }}
                            onInputChange={(_, val) => setContractorSearch(val)}
                            loading={contractorLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Contractor"
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px' },
                                        endAdornment: (
                                            <>
                                                {contractorLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    InputLabelProps={{ sx: { fontFamily } }}
                                />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            noOptionsText="Type to search contractors"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small" fullWidth label="Amount" required type="number"
                            value={form.amount}
                            onChange={e => handleChange('amount', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small" fullWidth label="Payment Date" type="date" required
                            value={form.payment_date}
                            onChange={e => handleChange('payment_date', e.target.value)}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            size="small" fullWidth label="Description" multiline rows={2}
                            value={form.description}
                            onChange={e => handleChange('description', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small" fullWidth label="Invoice Number"
                            value={form.invoice_number}
                            onChange={e => handleChange('invoice_number', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small" fullWidth select label="Payment Method"
                            value={form.payment_method}
                            onChange={e => handleChange('payment_method', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        >
                            {PAYMENT_METHODS.map(m => (
                                <MenuItem key={m.value} value={m.value} sx={{ fontFamily }}>{m.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            size="small" fullWidth label="Tax Year" type="number"
                            value={form.tax_year}
                            onChange={e => handleChange('tax_year', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            size="small" fullWidth select label="GL Expense Account"
                            value={form.gl_expense_account_id}
                            onChange={e => handleChange('gl_expense_account_id', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        >
                            <MenuItem value="" sx={{ fontFamily }}>— None —</MenuItem>
                            {expenseAccounts.map(a => (
                                <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} – {a.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            size="small" fullWidth select label="GL Cash Account"
                            value={form.gl_cash_account_id}
                            onChange={e => handleChange('gl_cash_account_id', e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        >
                            <MenuItem value="" sx={{ fontFamily }}>— None —</MenuItem>
                            {cashAccounts.map(a => (
                                <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} – {a.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !form.contractor_id || !form.amount}
                    sx={{
                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                        bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                    }}
                >
                    {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (editData ? 'Update' : 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
